import React, { useState, useEffect, useCallback } from 'react';
import { Plus, PencilSimple, Trash, ShieldCheck } from '@phosphor-icons/react';
import { Header } from '../../components/layout';
import { Modal, Button } from '../../components/common';
import { adminService } from '../../services';
import './Admin.css';

const MODULE_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'orders', label: 'Orders' },
  { key: 'customers', label: 'Customers' },
  { key: 'slots', label: 'Slots' },
  { key: 'reports', label: 'Reports' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'locations', label: 'Locations' },
  { key: 'admin', label: 'Admin' },
];

const DEFAULT_PERMISSIONS = {
  dashboard: true,
  orders: false,
  customers: false,
  slots: false,
  reports: false,
  pricing: false,
  locations: false,
  admin: false,
};

export function Admin({ onMenuClick }) {
  const [adminList, setAdminList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [roles, setRoles] = useState([]);
  const [stores, setStores] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    role: '',
    stores: [],
    permissions: { ...DEFAULT_PERMISSIONS },
    isAdmin: true,
  });

  const [roleFormData, setRoleFormData] = useState({
    role_name: '',
    permissions: { ...DEFAULT_PERMISSIONS },
  });

  const [isEditingRole, setIsEditingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getAdmins();
      setAdminList(data);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError('Failed to load admins. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRolesAndStores = useCallback(async () => {
    try {
      const [rolesData, storesData] = await Promise.all([
        adminService.getRoles(),
        adminService.getStores()
      ]);
      setRoles(rolesData);
      setStores(storesData);
    } catch (err) {
      console.error('Error fetching roles/stores:', err);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
    fetchRolesAndStores();
  }, [fetchAdmins, fetchRolesAndStores]);

  const handleAddAdmin = () => {
    setIsEditing(false);
    setSelectedAdmin(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      password: '',
      role: '',
      stores: [],
      permissions: { ...DEFAULT_PERMISSIONS },
      isAdmin: true,
    });
    setShowModal(true);
  };

  const handleEditAdmin = (admin) => {
    setIsEditing(true);
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      phone: admin.phone,
      email: admin.email || '',
      password: '',
      role: admin.role?._id || '',
      stores: admin.store?.map(s => s._id) || [],
      permissions: admin.permissions || { ...DEFAULT_PERMISSIONS },
      isAdmin: admin.isAdmin !== false,
    });
    setShowModal(true);
  };

  const handleDeleteClick = (admin) => {
    setSelectedAdmin(admin);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await adminService.deleteAdmin(selectedAdmin._id);
      setAdminList((prev) => prev.filter((s) => s._id !== selectedAdmin._id));
      setShowDeleteModal(false);
      setSelectedAdmin(null);
    } catch (err) {
      console.error('Error deleting admin:', err);
      alert('Failed to delete admin. Please try again.');
    }
  };

  const handleStoreToggle = (storeId) => {
    setFormData((prev) => ({
      ...prev,
      stores: prev.stores.includes(storeId)
        ? prev.stores.filter((s) => s !== storeId)
        : [...prev.stores, storeId],
    }));
  };

  const handleUserPermissionToggle = (key) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const adminData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        role: formData.role || undefined,
        store: formData.stores,
        permissions: formData.permissions,
        isAdmin: formData.isAdmin,
      };

      if (isEditing) {
        if (formData.password) {
          adminData.password = formData.password;
        }
        await adminService.updateAdmin(selectedAdmin._id, adminData);
      } else {
        adminData.password = formData.password;
        await adminService.createAdmin(adminData);
      }
      setShowModal(false);
      // Refresh the admin list to ensure it shows the latest data
      fetchAdmins();
    } catch (err) {
      console.error('Error saving admin:', err);
      alert(err.response?.data?.message || 'Failed to save admin. Please try again.');
    }
  };

  // Role management handlers
  const handleAddRole = () => {
    setIsEditingRole(false);
    setSelectedRole(null);
    setRoleFormData({
      role_name: '',
      permissions: { ...DEFAULT_PERMISSIONS },
    });
    setShowRoleModal(true);
  };

  const handleEditRole = (role) => {
    setIsEditingRole(true);
    setSelectedRole(role);
    setRoleFormData({
      role_name: role.role_name,
      permissions: role.permissions || { ...DEFAULT_PERMISSIONS },
    });
    setShowRoleModal(true);
  };

  const handlePermissionToggle = (key) => {
    setRoleFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditingRole) {
        const updatedRole = await adminService.updateRole(selectedRole._id, roleFormData);
        setRoles((prev) => prev.map((r) => (r._id === selectedRole._id ? updatedRole : r)));
      } else {
        const newRole = await adminService.createRole(roleFormData);
        setRoles((prev) => [...prev, newRole]);
      }
      setShowRoleModal(false);
      fetchAdmins();
    } catch (err) {
      console.error('Error saving role:', err);
      alert(err.response?.data?.message || 'Failed to save role. Please try again.');
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      'Delivery Agent': 'badge-info',
      'Pickup Agent': 'badge-warning',
      'Store Manager': 'badge-success',
      'Admin': 'badge-primary',
      'Super Admin': 'badge-primary',
    };
    return classes[role] || 'badge-neutral';
  };

  const getPermissionCount = (permissions) => {
    if (!permissions) return 0;
    return Object.values(permissions).filter(Boolean).length;
  };

  return (
    <>
      <Header
        title="Admin"
        subtitle="Manage admin users and their access permissions"
        onMenuClick={onMenuClick}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="secondary" icon={<ShieldCheck size={18} />} onClick={handleAddRole}>
              Add Role
            </Button>
            <Button icon={<Plus size={18} />} onClick={handleAddAdmin}>
              Add Admin
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && (
          <div className="error-banner" style={{ marginBottom: '1rem' }}>
            <span>{error}</span>
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {/* Roles Section */}
        <div className="admin-section">
          <h3 className="section-heading">Roles & Permissions</h3>
          <div className="table-container">
            <table className="data-table roles-table">
              <thead>
                <tr>
                  <th>Role Name</th>
                  <th>Module Access</th>
                  <th>Permissions</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role._id}>
                    <td>
                      <span className="role-name-cell">{role.role_name}</span>
                    </td>
                    <td>
                      <span className="module-count-badge">
                        {getPermissionCount(role.permissions)} modules
                      </span>
                    </td>
                    <td>
                      <div className="permissions-inline">
                        {MODULE_OPTIONS.filter(mod => role.permissions?.[mod.key]).map((mod) => (
                          <span key={mod.key} className="permission-tag active">
                            {mod.label}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="text-center">
                      <button
                        className="action-btn"
                        title="Edit Role"
                        onClick={() => handleEditRole(role)}
                      >
                        <PencilSimple size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Admin Users Section */}
        <div className="admin-section">
          <h3 className="section-heading">Admin Users</h3>
          {loading ? (
            <div className="loading">
              <div className="spinner" />
              <span style={{ marginTop: 12 }}>Loading admins...</span>
            </div>
          ) : adminList.length === 0 ? (
            <div className="empty-state">
              <p>No admin users found</p>
              <Button onClick={handleAddAdmin}>Add First Admin</Button>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Type</th>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminList.map((admin) => (
                    <tr key={admin._id}>
                      <td>
                        <div className="user-name-cell">
                          <div className="user-avatar">
                            {admin.name.split(' ').map((n) => n[0]).join('').substring(0, 2)}
                          </div>
                          <span className="user-name">{admin.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="user-phone">{admin.phone}</span>
                      </td>
                      <td>
                        <span className="user-email">{admin.email || '-'}</span>
                      </td>
                      <td>
                        <span className={`badge ${admin.isAdmin !== false ? 'badge-primary' : 'badge-neutral'}`}>
                          {admin.isAdmin !== false ? 'Admin' : 'Staff'}
                        </span>
                      </td>
                      <td>
                        {admin.role?.role_name ? (
                          <span className={`badge ${getRoleBadgeClass(admin.role?.role_name)}`}>
                            {admin.role.role_name}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <div className="permissions-inline">
                          {admin.permissions && MODULE_OPTIONS.filter(mod => admin.permissions[mod.key]).map((mod) => (
                            <span key={mod.key} className="permission-tag active">
                              {mod.label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="actions-cell">
                          <button
                            className="action-btn"
                            title="Edit"
                            onClick={() => handleEditAdmin(admin)}
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            className="action-btn danger"
                            title="Delete"
                            onClick={() => handleDeleteClick(admin)}
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Admin Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? 'Edit Admin' : 'Add Admin'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {isEditing ? 'Save Changes' : 'Add Admin'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Phone Number *</label>
            <input
              type="tel"
              className="form-input"
              placeholder="10-digit phone number"
              maxLength={10}
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter admin name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address (Optional)</label>
            <input
              type="email"
              className="form-input"
              placeholder="email@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{isEditing ? 'Password (leave blank to keep current)' : 'Password *'}</label>
            <input
              type="password"
              className="form-input"
              placeholder={isEditing ? 'Leave blank to keep current password' : 'Enter password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!isEditing}
            />
          </div>

          <div className="form-group">
            <label className="form-label">User Type *</label>
            <p className="form-hint">Admin users have system access. Staff members appear in pickup/delivery assignment.</p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="userType"
                  checked={formData.isAdmin === true}
                  onChange={() => setFormData({ ...formData, isAdmin: true })}
                />
                <span>Admin</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="userType"
                  checked={formData.isAdmin === false}
                  onChange={() => setFormData({ ...formData, isAdmin: false })}
                />
                <span>Staff</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Role (Optional)</label>
            <select
              className="form-select"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              <option value="">Select a role</option>
              {roles.map((role) => (
                <option key={role._id} value={role._id}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Module Access *</label>
            <p className="form-hint">Select which modules this user can access</p>
            <div className="permissions-checkbox-group">
              {MODULE_OPTIONS.map((mod) => (
                <label key={mod.key} className="permission-checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.permissions[mod.key]}
                    onChange={() => handleUserPermissionToggle(mod.key)}
                  />
                  <span>{mod.label}</span>
                </label>
              ))}
            </div>
          </div>

          {stores.length > 0 && (
            <div className="form-group">
              <label className="form-label">Assigned Stores</label>
              <div className="stores-checkbox-group">
                {stores.map((store) => (
                  <label key={store._id} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.stores.includes(store._id)}
                      onChange={() => handleStoreToggle(store._id)}
                    />
                    <span>{store.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </form>
      </Modal>

      {/* Add/Edit Role Modal */}
      <Modal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        title={isEditingRole ? 'Edit Role' : 'Add Role'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleRoleSubmit}>
              {isEditingRole ? 'Save Changes' : 'Add Role'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleRoleSubmit}>
          <div className="form-group">
            <label className="form-label">Role Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Store Manager, Delivery Agent"
              value={roleFormData.role_name}
              onChange={(e) => setRoleFormData({ ...roleFormData, role_name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Module Access</label>
            <p className="form-hint">Select which modules this role can access</p>
            <div className="permissions-checkbox-group">
              {MODULE_OPTIONS.map((mod) => (
                <label key={mod.key} className="permission-checkbox-item">
                  <input
                    type="checkbox"
                    checked={roleFormData.permissions[mod.key]}
                    onChange={() => handlePermissionToggle(mod.key)}
                  />
                  <span>{mod.label}</span>
                </label>
              ))}
            </div>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Admin"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{selectedAdmin?.name}</strong>?
          This action cannot be undone.
        </p>
      </Modal>
    </>
  );
}

export default Admin;
